<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import FormWrapper, { type FormContext } from "@kontextwerk/web-sdk/ui/layout/FormWrapper.svelte"
  import ModalDialog from "@kontextwerk/web-sdk/ui/overlay/ModalDialog.svelte"
  import CustomerCreateOverlay from "../../../../../_shared/_modules/customer-create-overlay"
  import CustomerAsyncMultiSelect from "../customer-async-multi-select"
  import { propertyCustomerRoleOptions } from "../../../../../../../_shared/_logic/customer-roles"
  import {
    propertyUnitTypeOptions,
    type PropertyUnitType,
  } from "../../../../../../../_shared/_logic/units"
  import type { Snippet } from "svelte"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  import type { UnitFormModel } from "./_logic/types"

  type Props = {
    unit?: UnitFormModel | null
    organizationId: string
    customers?: Customer[]
    customerIds?: string[]
    defaultRole?: PropertyCustomerRole | null
    onSaved?: (unit: UnitFormModel) => void
    darkMode?: boolean
    showCustomerAssignment?: boolean
    allowTypeEdit?: boolean
  }

  let {
    unit = null,
    organizationId,
    customers = [],
    customerIds = [],
    defaultRole = null,
    onSaved = () => {},
    onremoveOverlay = null,
    onshowOverlay = null,
    darkMode = true,
    showCustomerAssignment = true,
    allowTypeEdit = false,
    setActionButtonSnippet = null,
  }: Props & OverlayHostProps = $props()

  const fieldIds = {
    unitLabel: "unit-label",
    unitType: "unit-type",
    unitNumber: "unit-number",
    customerSelect: "unit-customers",
    roleSelect: "unit-role",
  }

  let showCustomerCreateModal = $state(false)
  let customerCreateActions = $state<Snippet | null>(null)

  const uniqueIds = (ids: (string | null | undefined)[]) =>
    Array.from(new Set((ids ?? []).map((id) => (id ?? "").trim()).filter(Boolean))) as string[]

  const upsertCustomer = (customer: Customer) => {
    const exists = customers.some((entry) => entry.id === customer.id)
    customers = exists
      ? customers.map((entry) => (entry.id === customer.id ? customer : entry))
      : [...customers, customer]
  }

  const handleCustomerCreated = (
    customer: Customer,
    data: UnitFormModel,
    updateData: (next: Partial<UnitFormModel>) => void,
  ) => {
    upsertCustomer(customer)
    const nextIds = uniqueIds([...(data.customer_ids ?? []), customer.id])
    updateData({ customer_ids: nextIds, role: data.role ?? defaultRole ?? "OTHER" })
    showCustomerCreateModal = false
  }

  const buildInitObject = (): UnitFormModel => ({
    id: unit?.id ?? null,
    client_id: unit?.client_id ?? unit?.id ?? null,
    unit_label: unit?.unit_label ?? "",
    unit_number: unit?.unit_number ?? null,
    notes: unit?.notes ?? null,
    unit_type: unit?.unit_type ?? "RESIDENTIAL",
    customer_ids: uniqueIds(unit?.customer_ids ?? customerIds ?? []),
    role: unit?.role ?? defaultRole ?? "OTHER",
  })

  const validateUnit = ({ data }: { data: UnitFormModel; form: HTMLFormElement | null }) => {
    const valid = Boolean(
      data.unit_label.trim() &&
      (showCustomerAssignment && data.customer_ids?.length ? data.role : true),
    )

    return valid
  }
  const submitUnit = async (data: UnitFormModel) => {
    onSaved?.({
      ...data,
      unit_number: data.unit_number?.trim() || null,
      unit_type: data.unit_type ?? "RESIDENTIAL",
      customer_ids: uniqueIds(data.customer_ids ?? []),
    })
    onremoveOverlay?.()
  }

  const close = (submitting: boolean) => {
    if (submitting) return

    onremoveOverlay?.()
  }

  let submitting = $state(false)
  let submitForm = $state<(() => Promise<void>) | null>(null)

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(actionButtons)
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })
</script>

<div class="overlay-form form" class:dark={darkMode} class:light={!darkMode}>
  <FormWrapper
    theme={darkMode ? "dark" : "light"}
    mode={unit ? "edit" : "create"}
    getInitObject={buildInitObject}
    validateData={validateUnit}
    onSubmit={submitUnit}
    submitLabel={unit ? "Speichern" : "Anlegen"}
    showReset={false}
    reinitKey={unit?.id ?? unit?.client_id ?? "new"}
    formSnippet={formContent}
    bind:submitting
    setSubmitFn={(fn) => (submitForm = fn)}
  />
</div>

{#snippet formContent(ctx: FormContext)}
  {@const { data, updateData, submitting } = ctx}
  <section class="row">
    <Input
      id={fieldIds.unitLabel}
      placeholder="Einheit/Name"
      bind:value={data.unit_label}
      brightBackground={!darkMode}
      disabled={submitting}
    />

    <Input
      id={fieldIds.unitType}
      type="select"
      placeholder="Einheitstyp"
      options={propertyUnitTypeOptions}
      bind:value={data.unit_type}
      brightBackground={!darkMode}
      disabled={submitting || Boolean(unit?.id) || !allowTypeEdit}
    />

    <Input
      id={fieldIds.unitNumber}
      placeholder="Nummer der Wohneinheit"
      bind:value={data.unit_number}
      brightBackground={!darkMode}
      disabled={submitting || Boolean(unit)}
    />
  </section>

  {#if showCustomerAssignment}
    <section class="row customer-row">
      <CustomerAsyncMultiSelect
        id={fieldIds.customerSelect}
        {organizationId}
        seedCustomers={customers}
        placeholder="Ansprechpartner auswählen"
        bind:value={data.customer_ids}
        disabled={submitting}
        brightBackground={!darkMode}
      />

      <Input
        id={fieldIds.roleSelect}
        type="select"
        placeholder="Rolle bzgl. Einheit"
        options={propertyCustomerRoleOptions}
        bind:value={data.role}
        brightBackground={!darkMode}
        disabled={submitting}
      />
    </section>
  {/if}
  <section class="row">
    <Input
      placeholder="Notiz (optional)"
      type="textarea"
      bind:value={data.notes}
      brightBackground={!darkMode}
      disabled={submitting}
    />
  </section>
  {#if showCustomerCreateModal}
    <ModalDialog
      appearance="plain"
      title="Ansprechpartner anlegen"
      onClose={() => (showCustomerCreateModal = false)}
    >
      {#snippet content()}
        <CustomerCreateOverlay
          {organizationId}
          setActionButtonSnippet={(snippet) => (customerCreateActions = snippet)}
          onCreated={(customer) => handleCustomerCreated(customer, data, updateData)}
          onremoveOverlay={() => (showCustomerCreateModal = false)}
        />
      {/snippet}
      {#snippet footerSnippet()}
        <div class="customer-create-actions">{@render customerCreateActions?.()}</div>
      {/snippet}
    </ModalDialog>
  {/if}
{/snippet}

{#snippet actionButtons()}
  <CTA
    type="secondaryButton"
    content="Abbrechen"
    onClick={() => close(submitting)}
    disabled={submitting}
  />
  <CTA
    type="primaryButton"
    content={submitting ? "Speichere…" : unit ? "Speichern" : "Anlegen"}
    onClick={() => submitForm?.()}
    disabled={submitting}
  />
{/snippet}

<style lang="less">
  .customer-create-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.6rem;
  }

  .overlay-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding-bottom: 0.5rem;
  }

  .row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 0.75rem;
  }

  .customer-row {
    grid-template-columns: 1fr auto;
    align-items: end;
  }

  :global(.overlay-form .formActions) {
    display: none;
  }
</style>
